import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faShip, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { globeService } from '../services/globeService';
import { getFlagUrl } from '../utils/flagUtils';
import './VesselSearchAutocomplete.css';

const DEBOUNCE_MS = 280;
const MIN_CHARS = 3;

// ``onQueryChange`` (optional) fires on every keystroke with the raw input
// value. Forms whose vessel field stays free text (the recap forms) use it to
// commit typed-but-not-picked names ("TBC", private vessels not in the AIS
// DB); selection still flows through ``onSelect``. Omit it for pick-only
// behaviour (the enquiry form).
const VesselSearchAutocomplete = ({ onSelect, onQueryChange, initialValue = '', placeholder = 'Search vessel by Name, IMO, MMSI or Call Sign...' }) => {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  // 'idle' | 'empty' (valid search, zero matches) | 'error' (request failed).
  // Drives a calm inline message instead of a red error toast.
  const [searchState, setSearchState] = useState('idle');
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const detectIdentifierType = (value) => {
    const trimmed = value.replace(/\s/g, '');
    if (/^\d{7}$/.test(trimmed)) return 'IMO';
    if (/^\d{9}$/.test(trimmed)) return 'MMSI';
    return 'Name';
  };

  const doSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < MIN_CHARS) {
      setResults([]);
      setIsOpen(false);
      setSearchState('idle');
      return;
    }
    setIsLoading(true);
    try {
      const data = await globeService.searchVessels(searchQuery);
      setResults(data);
      setHighlightIndex(-1);
      // A genuine zero-match result is NOT an error: keep the dropdown open and
      // show a calm "no vessels found" hint rather than failing silently.
      setSearchState(data.length > 0 ? 'idle' : 'empty');
      setIsOpen(true);
    } catch (err) {
      console.error('Search failed:', err);
      setResults([]);
      setSearchState('error');
      setIsOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (onQueryChange) onQueryChange(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), DEBOUNCE_MS);
  };

  const handleSelect = (vessel) => {
    setQuery(vessel.ship_name || '');
    setIsOpen(false);
    setResults([]);
    setSearchState('idle');
    if (onSelect) onSelect(vessel);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(results[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Keep the input in sync when the parent supplies/changes the vessel name
  // after mount. The recap auto-populate (and a vessel selection) set
  // ``formData.vessel_name`` asynchronously, so seeding ``query`` from
  // ``initialValue`` at mount alone left the box empty. ``initialValue`` only
  // changes on those events -- never on each keystroke -- so this never clobbers
  // what the user is actively typing.
  useEffect(() => {
    setQuery(initialValue || '');
  }, [initialValue]);

  const idType = query ? detectIdentifierType(query) : null;

  return (
    <div className="vessel-search-container" ref={containerRef}>
      <div className="vessel-search-input-wrapper">
        <FontAwesomeIcon icon={faSearch} className="vessel-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="vessel-search-input"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0 || searchState !== 'idle') setIsOpen(true); }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {isLoading && <FontAwesomeIcon icon={faSpinner} spin className="vessel-search-spinner" />}
        {idType && query.trim().length >= MIN_CHARS && (
          <span className="vessel-search-id-badge">{idType}</span>
        )}
      </div>

      {isOpen && results.length === 0 && searchState === 'empty' && !isLoading && (
        <div className="vessel-search-dropdown vessel-search-message">
          <FontAwesomeIcon icon={faShip} className="vessel-search-message-icon" />
          <span className="vessel-search-message-text">
            No vessels found &mdash; try an IMO or MMSI
          </span>
        </div>
      )}

      {isOpen && results.length === 0 && searchState === 'error' && !isLoading && (
        <div className="vessel-search-dropdown vessel-search-message vessel-search-message-error">
          <FontAwesomeIcon icon={faSearch} className="vessel-search-message-icon" />
          <span className="vessel-search-message-text">
            Search is temporarily unavailable. Please try again.
          </span>
        </div>
      )}

      {isOpen && results.length > 0 && (
        <ul className="vessel-search-dropdown">
          {results.map((vessel, index) => (
            <li
              key={`${vessel.imo}-${vessel.mmsi}-${index}`}
              className={`vessel-search-item ${index === highlightIndex ? 'vessel-search-item-highlighted' : ''}`}
              onClick={() => handleSelect(vessel)}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              <div className="vessel-search-item-left">
                {vessel.flag && getFlagUrl(vessel.flag) ? (
                  <img
                    src={getFlagUrl(vessel.flag)}
                    alt={vessel.flag}
                    className="vessel-search-flag"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <FontAwesomeIcon icon={faShip} className="vessel-search-ship-icon" />
                )}
                <div className="vessel-search-item-info">
                  <span className="vessel-search-item-name">{vessel.ship_name}</span>
                  <span className="vessel-search-item-type">{vessel.ship_type}</span>
                </div>
              </div>
              <div className="vessel-search-item-right">
                {vessel.imo && <span className="vessel-search-item-id">IMO: {vessel.imo}</span>}
                {vessel.mmsi && <span className="vessel-search-item-id">MMSI: {vessel.mmsi}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default VesselSearchAutocomplete;
